/**
 * Excel/CSV IO kit (F3-J) — transport helpers for the wizard and the export
 * buttons. JSON endpoints travel over the typed client (`api`); the upload
 * multipart and attachment downloads use `ioFetchRaw` (same auth).
 */

import { ioFetchRaw } from "@/lib/api";
import type { components } from "@space/shared";

export type IoEntity = components["schemas"]["IoEntityDto"];
export type IoEntitiesResponse = components["schemas"]["IoEntitiesResponse"];
export type ImportJobResponse = components["schemas"]["ImportJobResponse"];
export type ImportRowIssue = components["schemas"]["ImportRowIssue"];
export type MappingProposal = components["schemas"]["MappingProposal"];

export const ioQueryKeys = {
  entities: (orgId: string | undefined) => ["io", "entities", orgId ?? "platform"] as const,
  job: (id: string | undefined) => ["io", "import", id ?? "none"] as const,
  issues: (id: string, page: number) => ["io", "import", id, "issues", page] as const,
};

/** Uploads the file and creates the job (multipart {entityKey, orgId?, file}). */
export async function uploadImportFile(
  entityKey: string,
  orgId: string | undefined,
  file: File,
): Promise<ImportJobResponse> {
  const form = new FormData();
  form.append("entityKey", entityKey);
  if (orgId) {
    form.append("orgId", orgId);
  }
  // No manual Content-Type: the browser adds the multipart boundary.
  form.append("file", file);
  const res = await ioFetchRaw("/api/io/import", { method: "POST", body: form });
  if (!res.ok) {
    throw new Error(await extractError(res));
  }
  const data: unknown = await res.json();
  if (!isImportJobResponse(data)) {
    throw new Error("Unexpected import response shape");
  }
  return data;
}

/** Minimal type guard for the server response (avoids unsafe asserts). */
function isImportJobResponse(v: unknown): v is ImportJobResponse {
  return typeof v === "object" && v !== null && "job" in v && typeof v.job === "object";
}

/** Downloads an authenticated attachment (xlsx/csv export, error report). */
export async function downloadIoFile(path: string, fallbackName: string): Promise<void> {
  const res = await ioFetchRaw(path);
  if (!res.ok) {
    throw new Error(await extractError(res));
  }
  const blob = await res.blob();
  const disposition = res.headers.get("content-disposition") ?? "";
  const match = /filename="([^"]+)"/.exec(disposition);
  const name = match?.[1] ?? fallbackName;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.append(a);
  a.click();
  a.remove();
  // Deferred revocation: give the browser time to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Builds the export URL with format + filters as query params. */
export function buildExportPath(
  entityKey: string,
  format: "xlsx" | "csv",
  orgId: string | undefined,
  filters: Record<string, string | undefined>,
): string {
  const params = new URLSearchParams({ format });
  if (orgId) {
    params.set("orgId", orgId);
  }
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "") {
      params.set(k, v);
    }
  }
  return `/api/io/export/${encodeURIComponent(entityKey)}?${params.toString()}`;
}

/** Builds the URL of a multi-sheet workbook (xlsx) with filters as query. */
export function buildWorkbookPath(
  workbookKey: string,
  orgId: string | undefined,
  filters: Record<string, string | undefined>,
): string {
  const params = new URLSearchParams();
  if (orgId) {
    params.set("orgId", orgId);
  }
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== "") {
      params.set(k, v);
    }
  }
  const qs = params.toString();
  return `/api/io/export/workbook/${encodeURIComponent(workbookKey)}${qs ? `?${qs}` : ""}`;
}

async function extractError(res: Response): Promise<string> {
  try {
    const body: unknown = await res.json();
    if (body && typeof body === "object" && "error" in body) {
      const err = (body as { error: unknown }).error;
      if (typeof err === "string") {
        return err;
      }
      if (err && typeof err === "object" && "message" in err) {
        return String((err as { message: unknown }).message);
      }
    }
  } catch {
    // non-JSON body — falls back to the generic one
  }
  return `HTTP ${res.status}`;
}
